<template>
  <div class="profile-page">
    <!-- Profile Header -->
    <div class="profile-header mb-4">
      <b-card class="border-0 shadow-lg profile-cover-card" no-body>
        <div class="profile-cover">
          <div class="cover-image" :style="{ backgroundImage: 'url(' + (form.cover_image || defaultCoverImage) + ')' }">
            <div class="cover-overlay"></div>
          </div>
          <div class="profile-content">
            <div class="profile-avatar-wrapper">
              <input
                ref="profileFileInput"
                type="file"
                accept="image/*"
                class="d-none"
                @change="onProfileFileSelected"
              />
              <input
                ref="coverFileInput"
                type="file"
                accept="image/*"
                class="d-none"
                @change="onCoverFileSelected"
              />
              <div
                class="profile-avatar cursor-pointer"
                @click="$refs.profileFileInput.click()"
              >
                <b-avatar
                  size="120"
                  variant="light-primary"
                  :src="form.profile_image || ''"
                  class="avatar-main"
                >
                  <feather-icon v-if="!form.profile_image" icon="UserIcon" size="60" />
                </b-avatar>
                <div class="avatar-edit-overlay">
                  <feather-icon icon="CameraIcon" size="20" />
                  <span class="d-block small mt-1">Change Photo</span>
                </div>
              </div>
            </div>
            <div class="profile-info">
              <h2 class="profile-name text-white mb-1">
                {{ form.first_name }} {{ form.last_name || 'User' }}
              </h2>
              <p class="profile-title text-white mb-2">{{ form.job_title || 'Professional' }}</p>
              <div class="profile-stats text-white">
                <div class="stat-item">
                  <span class="stat-number">{{ form.projects_count || 0 }}</span>
                  <span class="stat-label">Projects</span>
                </div>
                <div class="stat-item">
                  <span class="stat-number">{{ form.experience_years || 0 }}</span>
                  <span class="stat-label">Years Exp</span>
                </div>
                <div class="stat-item">
                  <span class="stat-number">{{ form.rating || 4.5 }}</span>
                  <span class="stat-label">Rating</span>
                </div>
              </div>
            </div>
            <div class="profile-actions">
              <b-button variant="light" size="sm" class="mr-2" @click="$refs.coverFileInput.click()">
                <feather-icon icon="CameraIcon" class="mr-50" />
                Change Cover
              </b-button>
              <b-button variant="primary" size="sm" @click="editMode = !editMode">
                <feather-icon icon="EditIcon" class="mr-50" />
                {{ editMode ? 'View Mode' : 'Edit Profile' }}
              </b-button>
            </div>
          </div>
        </div>
      </b-card>
    </div>

    <!-- Profile Content -->
    <b-row>
      <!-- Main Content -->
      <b-col lg="8">
        <!-- Personal Information -->
        <b-card class="shadow-sm border-0 mb-4 overflow-hidden" no-body>
          <b-card-header class="border-0 bg-info p-3">
            <div class="d-flex align-items-center">
              <div class="bg-white rounded p-1 mr-2 d-flex align-items-center justify-content-center" style="width:36px;height:36px;">
                <feather-icon icon="UserIcon" class="text-info" size="20" />
              </div>
              <div>
                <h5 class="mb-0 font-weight-bolder text-white">Personal Information</h5>
                <small class="text-white" style="opacity:0.8">Update your personal details</small>
              </div>
            </div>
          </b-card-header>
          <b-card-body class="pt-3 pb-3">
            <b-form>
              <b-row>
                <b-col md="6">
                  <b-form-group label="First Name" label-for="first_name" label-class="font-weight-bold small text-uppercase text-muted">
                    <b-form-input
                      id="first_name"
                      v-model="form.first_name"
                      placeholder="Enter first name"
                      :disabled="!editMode"
                      size="sm"
                    />
                  </b-form-group>
                </b-col>
                <b-col md="6">
                  <b-form-group label="Last Name" label-for="last_name" label-class="font-weight-bold small text-uppercase text-muted">
                    <b-form-input
                      id="last_name"
                      v-model="form.last_name"
                      placeholder="Enter last name"
                      :disabled="!editMode"
                      size="sm"
                    />
                  </b-form-group>
                </b-col>
                <b-col md="6">
                  <b-form-group label="Email Address" label-for="email" label-class="font-weight-bold small text-uppercase text-muted">
                    <b-form-input
                      id="email"
                      v-model="form.email"
                      type="email"
                      placeholder="Enter email"
                      :disabled="!editMode"
                      size="sm"
                    />
                  </b-form-group>
                </b-col>
                <b-col md="6">
                  <b-form-group label="Phone Number" label-for="phone" label-class="font-weight-bold small text-uppercase text-muted">
                    <b-form-input
                      id="phone"
                      v-model="form.phone"
                      placeholder="Enter phone number"
                      :disabled="!editMode"
                      size="sm"
                    />
                  </b-form-group>
                </b-col>
                <b-col md="6">
                  <b-form-group label="Date of Birth" label-for="date_of_birth" label-class="font-weight-bold small text-uppercase text-muted">
                    <b-form-input
                      id="date_of_birth"
                      v-model="form.date_of_birth"
                      type="date"
                      :disabled="!editMode"
                      size="sm"
                    />
                  </b-form-group>
                </b-col>
                <b-col md="6">
                  <b-form-group label="Gender" label-for="gender" label-class="font-weight-bold small text-uppercase text-muted">
                    <b-form-select
                      id="gender"
                      v-model="form.gender"
                      :options="genderOptions"
                      :disabled="!editMode"
                      size="sm"
                    />
                  </b-form-group>
                </b-col>
                <b-col cols="12">
                  <b-form-group label="Bio" label-for="bio" label-class="font-weight-bold small text-uppercase text-muted">
                    <b-form-textarea
                      id="bio"
                      v-model="form.bio"
                      placeholder="Tell us about yourself..."
                      rows="4"
                      :disabled="!editMode"
                      size="sm"
                    />
                  </b-form-group>
                </b-col>
                <b-col md="6">
                  <b-form-group label="Country" label-for="country" label-class="font-weight-bold small text-uppercase text-muted">
                    <b-form-select
                      id="country"
                      v-model="form.country"
                      :options="countryOptions"
                      :disabled="!editMode"
                      size="sm"
                    />
                  </b-form-group>
                </b-col>
                <b-col md="6">
                  <b-form-group label="City" label-for="city" label-class="font-weight-bold small text-uppercase text-muted">
                    <b-form-input
                      id="city"
                      v-model="form.city"
                      placeholder="Enter city"
                      :disabled="!editMode"
                      size="sm"
                    />
                  </b-form-group>
                </b-col>
                <b-col cols="12">
                  <b-form-group label="Address" label-for="address" label-class="font-weight-bold small text-uppercase text-muted">
                    <b-form-input
                      id="address"
                      v-model="form.address"
                      placeholder="Enter address"
                      :disabled="!editMode"
                      size="sm"
                    />
                  </b-form-group>
                </b-col>
              </b-row>
            </b-form>
          </b-card-body>
        </b-card>

        <!-- Professional Information -->
        <b-card class="shadow-sm border-0 mb-4 overflow-hidden" no-body>
          <b-card-header class="border-0 bg-success p-3">
            <div class="d-flex align-items-center">
              <div class="bg-white rounded p-1 mr-2 d-flex align-items-center justify-content-center" style="width:36px;height:36px;">
                <feather-icon icon="BriefcaseIcon" class="text-success" size="20" />
              </div>
              <div>
                <h5 class="mb-0 font-weight-bolder text-white">Professional Information</h5>
                <small class="text-white" style="opacity:0.8">Company & Role</small>
              </div>
            </div>
          </b-card-header>
          <b-card-body class="pt-3 pb-3">
            <b-form>
              <b-row>
                <b-col md="6">
                  <b-form-group label="Job Title" label-class="font-weight-bold small text-uppercase text-muted">
                    <b-form-input v-model="form.job_title" :disabled="!editMode" size="sm" />
                  </b-form-group>
                </b-col>
                <b-col md="6">
                  <b-form-group label="Company" label-class="font-weight-bold small text-uppercase text-muted">
                    <b-form-input v-model="form.company" :disabled="!editMode" size="sm" />
                  </b-form-group>
                </b-col>
                <b-col md="6">
                  <b-form-group label="Department" label-class="font-weight-bold small text-uppercase text-muted">
                    <b-form-input v-model="form.department" :disabled="!editMode" size="sm" />
                  </b-form-group>
                </b-col>
                <b-col md="6">
                  <b-form-group label="Experience (Years)" label-class="font-weight-bold small text-uppercase text-muted">
                    <b-form-input
                      v-model="form.experience_years"
                      type="number"
                      placeholder="Years of experience"
                      :disabled="!editMode"
                      size="sm"
                    />
                  </b-form-group>
                </b-col>
                <b-col md="6">
                  <b-form-group label="LinkedIn Profile" label-for="linkedin" label-class="font-weight-bold small text-uppercase text-muted">
                    <b-form-input
                      id="linkedin"
                      v-model="form.linkedin"
                      placeholder="LinkedIn profile URL"
                      :disabled="!editMode"
                      size="sm"
                    />
                  </b-form-group>
                </b-col>
                <b-col md="6">
                  <b-form-group label="Portfolio Website" label-for="portfolio" label-class="font-weight-bold small text-uppercase text-muted">
                    <b-form-input
                      id="portfolio"
                      v-model="form.portfolio"
                      placeholder="Portfolio website URL"
                      :disabled="!editMode"
                      size="sm"
                    />
                  </b-form-group>
                </b-col>
                <b-col cols="12">
                  <b-form-group label="Skills" label-for="skills" label-class="font-weight-bold small text-uppercase text-muted">
                    <b-form-tags
                      id="skills"
                      v-model="form.skills"
                      placeholder="Add skills..."
                      :disabled="!editMode"
                      size="sm"
                    />
                  </b-form-group>
                </b-col>
              </b-row>
            </b-form>
          </b-card-body>
        </b-card>

      </b-col>

      <!-- Sidebar -->
      <b-col lg="4">
        <!-- Subscription Card -->
        <b-card class="shadow-sm border-0 mb-4 overflow-hidden" no-body>
          <b-card-header class="border-0 bg-primary p-3">
            <div class="d-flex align-items-center">
              <div class="bg-white rounded p-1 mr-2 d-flex align-items-center justify-content-center" style="width:36px;height:36px;">
                <feather-icon icon="CreditCardIcon" class="text-primary" size="20" />
              </div>
              <div>
                <h5 class="mb-0 font-weight-bolder text-white">Current Plan</h5>
                <small class="text-white" style="opacity:0.8">Your subscription package</small>
              </div>
            </div>
          </b-card-header>
          <b-card-body class="pt-3 pb-3">
            <!-- Plan Price Header -->
            <div class="text-center mb-3 p-3 rounded" style="background:#f8f9fa;border:1px solid #e9ecef;">
              <h3 class="text-primary font-weight-bolder mb-1">{{ form.current_plan_name || 'Free Plan' }}</h3>
              <div class="d-flex align-items-baseline justify-content-center">
                <span class="h4 font-weight-bolder text-dark mb-0">${{ form.current_plan_price || '0' }}</span>
                <span class="text-muted ml-1">/mo</span>
              </div>
              <b-badge
                pill
                class="mt-2 px-3 py-1"
                :variant="form.subscription_is_active ? 'light-success' : 'light-danger'"
              >
                <feather-icon
                  :icon="form.subscription_is_active ? 'CheckCircleIcon' : 'XCircleIcon'"
                  size="12"
                  class="mr-50"
                />
                {{ form.subscription_is_active ? 'Active' : 'Inactive' }}
              </b-badge>
            </div>

            <!-- Details List -->
            <div class="subscription-details mb-3">
              <div class="d-flex align-items-center py-2 px-2 rounded" style="background:#f8f9fa;">
                <feather-icon icon="CalendarIcon" size="16" class="text-muted mr-2" />
                <span class="text-muted small flex-grow-1">Start Date</span>
                <span class="font-weight-bold small text-dark">{{ form.subscription_start_date || '-' }}</span>
              </div>
              <div class="d-flex align-items-center py-2 px-2 rounded mt-1" style="background:#f8f9fa;">
                <feather-icon icon="ClockIcon" size="16" class="text-muted mr-2" />
                <span class="text-muted small flex-grow-1">End Date</span>
                <span class="font-weight-bold small text-dark">{{ form.subscription_end_date || '-' }}</span>
              </div>
              <div class="d-flex align-items-center py-2 px-2 rounded mt-1" style="background:#f8f9fa;">
                <feather-icon icon="UsersIcon" size="16" class="text-muted mr-2" />
                <span class="text-muted small flex-grow-1">Max Leads / Month</span>
                <b-badge variant="light-primary" class="font-weight-bold">{{ form.subscription_max_leads }}</b-badge>
              </div>
              <div class="d-flex align-items-center py-2 px-2 rounded mt-1" style="background:#f8f9fa;">
                <feather-icon icon="MapPinIcon" size="16" class="text-muted mr-2" />
                <span class="text-muted small flex-grow-1">Max States</span>
                <b-badge variant="light-primary" class="font-weight-bold">{{ form.subscription_max_states }}</b-badge>
              </div>
            </div>

            <hr class="my-3" style="border-style:dashed;" />

            <!-- Change Plan -->
            <b-form-group label="Change Plan" label-class="font-weight-bold small text-uppercase text-muted">
              <b-form-select
                v-model="form.selected_plan"
                :options="planOptions"
                size="sm"
              />
            </b-form-group>
            <b-button variant="primary" block class="shadow-sm" @click="updatePlan" :disabled="!editMode || saving">
              <feather-icon v-if="!saving" icon="RefreshCwIcon" size="14" class="mr-50" />
              <b-spinner v-if="saving" small class="mr-1" />
              Update Plan
            </b-button>
          </b-card-body>
        </b-card>

        <!-- Preferences -->
        <b-card class="shadow-sm border-0 mb-4 overflow-hidden" no-body>
          <b-card-header class="border-0 bg-warning p-3">
            <div class="d-flex align-items-center">
              <div class="bg-white rounded p-1 mr-2 d-flex align-items-center justify-content-center" style="width:36px;height:36px;">
                <feather-icon icon="SettingsIcon" class="text-warning" size="20" />
              </div>
              <div>
                <h5 class="mb-0 font-weight-bolder text-white">Preferences</h5>
                <small class="text-white" style="opacity:0.8">Customize your experience</small>
              </div>
            </div>
          </b-card-header>
          <b-card-body class="pt-3 pb-3">
            <b-form-group label="Language" label-class="font-weight-bold small text-uppercase text-muted">
              <b-form-select
                v-model="form.language"
                :options="languageOptions"
                size="sm"
                :disabled="!editMode"
              />
            </b-form-group>
            <b-form-group label="Timezone" label-class="font-weight-bold small text-uppercase text-muted">
              <b-form-select
                v-model="form.timezone"
                :options="timezoneOptions"
                size="sm"
                :disabled="!editMode"
              />
            </b-form-group>
            <b-form-group label="Email Notifications" label-class="font-weight-bold small text-uppercase text-muted">
              <b-form-checkbox
                v-model="form.email_notifications"
                switch
                :disabled="!editMode"
              >
                Receive email notifications
              </b-form-checkbox>
            </b-form-group>
            <b-form-group label="Push Notifications" label-class="font-weight-bold small text-uppercase text-muted">
              <b-form-checkbox
                v-model="form.push_notifications"
                switch
                :disabled="!editMode"
              >
                Receive push notifications
              </b-form-checkbox>
            </b-form-group>
          </b-card-body>
        </b-card>

      </b-col>
    </b-row>

    <!-- Action Buttons -->
    <div class="action-buttons position-fixed bottom-0 right-0 p-3" v-if="editMode">
      <b-button variant="secondary" @click="cancelEdit" class="mr-2">
        Cancel
      </b-button>
      <b-button variant="primary" @click="updateProfile" :disabled="saving">
        <b-spinner v-if="saving" small class="mr-1" />
        Save Changes
      </b-button>
    </div>
  </div>
</template>

<script>
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import { mapActions } from "vuex";

export default {
  name: "Profile",
  data() {
    return {
      saving: false,
      editMode: false,
      defaultCoverImage: 'https://picsum.photos/seed/cover/1200/300.jpg',
      form: {
        // Personal Information
        first_name: "",
        last_name: "",
        email: "",
        phone: "",
        date_of_birth: "",
        gender: null,
        bio: "",
        country: null,
        city: "",
        address: "",
        profile_image: null,
        profile_image_file: null,
        cover_image: null,
        
        // Professional Information
        job_title: "",
        company: "",
        department: "",
        experience_years: null,
        linkedin: "",
        portfolio: "",
        skills: [],
        projects_count: 0,
        rating: 4.5,
        
        // Subscription
        current_plan_name: "",
        current_plan_price: 0,
        selected_plan: null,
        subscription_start_date: "",
        subscription_end_date: "",
        subscription_max_leads: 0,
        subscription_max_states: 0,
        subscription_is_active: false,

        // Agent Profile / Account
        username: "",
        role_name: "",
        eligibility_status: "",
        is_active: false,
        onboarding_completed: false,
        last_login_at: "",
        external_id: "",
        agent_full_name: "",
        agent_agency_name: "",
        agent_business_email: "",
        agent_business_phone: "",
        agent_compliance_attested: false,
        agent_license_type: "",
        agent_npn_number: "",
        agent_primary_targeting_mode: "",
        agent_products: "",
        agent_licensed_states: "",
        agent_target_states: "",
        agent_target_zip: "",

        // Preferences
        language: "en",
        timezone: "UTC",
        email_notifications: true,
        push_notifications: true,
      },
      genderOptions: [
        { value: null, text: "Select gender..." },
        { value: "male", text: "Male" },
        { value: "female", text: "Female" },
        { value: "other", text: "Other" },
        { value: "prefer_not_to_say", text: "Prefer not to say" }
      ],
      countryOptions: [
        { value: null, text: "Select country..." },
        { value: "us", text: "United States" },
        { value: "uk", text: "United Kingdom" },
        { value: "ca", text: "Canada" },
        { value: "au", text: "Australia" },
        { value: "de", text: "Germany" },
        { value: "fr", text: "France" },
        { value: "jp", text: "Japan" },
        { value: "cn", text: "China" },
        { value: "in", text: "India" },
        { value: "pk", text: "Pakistan" }
      ],
      languageOptions: [
        { value: "en", text: "English" },
        { value: "es", text: "Spanish" },
        { value: "fr", text: "French" },
        { value: "de", text: "German" },
        { value: "zh", text: "Chinese" },
        { value: "ja", text: "Japanese" },
        { value: "ar", text: "Arabic" }
      ],
      timezoneOptions: [
        { value: "UTC", text: "UTC (Coordinated Universal Time)" },
        { value: "EST", text: "EST (Eastern Standard Time)" },
        { value: "CST", text: "CST (Central Standard Time)" },
        { value: "MST", text: "MST (Mountain Standard Time)" },
        { value: "PST", text: "PST (Pacific Standard Time)" },
        { value: "GMT", text: "GMT (Greenwich Mean Time)" },
        { value: "CET", text: "CET (Central European Time)" },
        { value: "JST", text: "JST (Japan Standard Time)" }
      ],
      planOptions: []
    };
  },
  mounted() {
    this.loadPlaceholderData();
    this.loadSubscriptionPlans();
  },
  methods: {
    ...mapActions({
      getSubscriptionPlans: "appData/getSubscriptionPlans",
    }),
    onProfileFileSelected(event) {
      const file = event.target.files && event.target.files[0];
      if (!file || !file.type.startsWith("image/")) return;
      
      this.form.profile_image_file = file;
      this.form.profile_image = URL.createObjectURL(file);
      event.target.value = "";
    },
    onCoverFileSelected(event) {
      const file = event.target.files && event.target.files[0];
      if (!file || !file.type.startsWith("image/")) return;
      
      this.form.cover_image_file = file;
      this.form.cover_image = URL.createObjectURL(file);
      event.target.value = "";
    },
    loadPlaceholderData() {
      const user = this.$store.getters["appData/getLoggedInUser"];
      if (!user) return;

      const ap = user.agent_profile || {};

      // --- Personal Information ---
      // Try to split full_name into first/last, or use existing fields
      if (ap.full_name) {
        const parts = ap.full_name.split(" ");
        this.form.first_name = parts[0] || "";
        this.form.last_name = parts.slice(1).join(" ") || "";
      } else {
        this.form.first_name = user.first_name || "";
        this.form.last_name = user.last_name || "";
      }
      this.form.email = user.email || ap.business_email || "";
      this.form.phone = user.mobile_number || ap.business_phone || user.phone || "";
      this.form.profile_image = user.profile_image || null;
      this.form.bio = ap.full_name ? `Licensed agent - ${ap.license_type || "individual"}` : "";
      this.form.city = user.city || "";
      this.form.country = user.country || null;
      this.form.address = user.address || "";

      // --- Professional Information ---
      this.form.job_title = ap.license_type ? `${ap.license_type} Agent` : "Insurance Agent";
      this.form.company = ap.agency_name || "";
      this.form.department = ap.primary_targeting_mode || "";
      this.form.experience_years = user.experience_years || null;
      this.form.linkedin = user.linkedin || "";
      this.form.portfolio = user.portfolio || "";
      this.form.skills = Array.isArray(ap.products) ? ap.products : (Array.isArray(ap.services_enabled) ? ap.services_enabled : []);
      this.form.projects_count = user.projects_count || 0;
      this.form.rating = user.rating || 4.5;

      // --- Subscription ---
      const sub = user.subscription || {};
      this.form.current_plan_name = sub.plan_name || user.current_plan_name || "Free Plan";
      this.form.current_plan_price = sub.plan_price !== undefined ? sub.plan_price : (user.current_plan_price || 0);
      this.form.subscription_start_date = sub.start_date || "";
      this.form.subscription_end_date = sub.end_date || "";
      this.form.subscription_max_leads = sub.max_leads_per_month !== undefined ? sub.max_leads_per_month : 0;
      this.form.subscription_max_states = sub.max_states !== undefined ? sub.max_states : 0;
      this.form.subscription_is_active = sub.is_active !== undefined ? sub.is_active : false;
    },
    async loadSubscriptionPlans() {
      try {
        const response = await this.getSubscriptionPlans();
        if (response.status === 200 && Array.isArray(response.data.results || response.data)) {
          const plans = response.data.results || response.data;
          this.planOptions = [
            { value: null, text: "Select plan..." },
            ...plans.map(plan => ({
              value: plan.id,
              text: `${plan.name} - $${plan.price || 0}/mo`,
              name: plan.name,
              price: plan.price || 0,
            })),
          ];
        }
      } catch (error) {
        console.error("Error loading subscription plans:", error);
      }
    },
    cancelEdit() {
      this.editMode = false;
      this.loadPlaceholderData(); // Reset to original data
    },
    updateProfile() {
      this.saving = true;
      setTimeout(() => {
        this.saving = false;
        this.editMode = false;
        this.$toast({
          component: ToastificationContent,
          props: {
            title: "Profile updated successfully!",
            variant: "success",
            icon: "CheckIcon",
          },
        }, { position: "top-right" });
      }, 1000);
    },
    updatePlan() {
      this.saving = true;
      setTimeout(() => {
        this.saving = false;
        const selectedPlan = this.planOptions.find(plan => plan.value === this.form.selected_plan);
        if (selectedPlan) {
          const planName = selectedPlan.name || selectedPlan.text;
          this.form.current_plan_name = planName;
          this.form.current_plan_price = selectedPlan.price || 0;
          
          this.$toast({
            component: ToastificationContent,
            props: {
              title: `Plan updated to ${planName}`,
              variant: "success",
              icon: "CheckIcon",
            },
          }, { position: "top-right" });
        }
      }, 1000);
    }
  }
};
</script>

<style scoped>
.profile-page {
  min-height: 100vh;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  padding: 1rem;
}

/* Profile Header Styles */
.profile-header .profile-cover-card {
  overflow: hidden;
  border-radius: 15px;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.profile-header .profile-cover-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
}

.profile-header .profile-cover {
  position: relative;
  height: 300px;
}

.profile-header .profile-cover .cover-image {
  width: 100%;
  height: 100%;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  position: relative;
}

.profile-header .profile-cover .cover-image .cover-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(to bottom, rgba(0, 0, 0, 0.1), rgba(0, 0, 0, 0.6));
}

.profile-header .profile-cover .profile-content {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 2rem;
  display: flex;
  align-items: flex-end;
  gap: 2rem;
}

.profile-header .profile-cover .profile-avatar-wrapper {
  position: relative;
}

.profile-header .profile-cover .profile-avatar-wrapper .profile-avatar {
  position: relative;
  border: 4px solid white;
  border-radius: 50%;
  overflow: hidden;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.profile-header .profile-cover .profile-avatar-wrapper .profile-avatar:hover {
  transform: scale(1.05);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
}

.profile-header .profile-cover .profile-avatar-wrapper .profile-avatar:hover .avatar-edit-overlay {
  opacity: 1;
}

.profile-header .profile-cover .profile-avatar-wrapper .profile-avatar .avatar-main {
  border: none;
}

.profile-header .profile-cover .profile-avatar-wrapper .profile-avatar .avatar-edit-overlay {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: white;
  opacity: 0;
  transition: opacity 0.3s ease;
  border-radius: 50%;
}

.profile-header .profile-cover .profile-info {
  flex: 1;
  color: white;
}

.profile-header .profile-cover .profile-info .profile-name {
  font-size: 2rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.3);
}

.profile-header .profile-cover .profile-info .profile-title {
  font-size: 1.1rem;
  opacity: 0.9;
  margin-bottom: 1rem;
}

.profile-header .profile-cover .profile-info .profile-stats {
  display: flex;
  gap: 2rem;
}

.profile-header .profile-cover .profile-info .profile-stats .stat-item {
  text-align: center;
}

.profile-header .profile-cover .profile-info .profile-stats .stat-item .stat-number {
  display: block;
  font-size: 1.5rem;
  font-weight: 700;
  line-height: 1;
}

.profile-header .profile-cover .profile-info .profile-stats .stat-item .stat-label {
  font-size: 0.8rem;
  opacity: 0.8;
  text-transform: uppercase;
  letter-spacing: 1px;
}

.profile-header .profile-cover .profile-actions {
  display: flex;
  gap: 1rem;
  align-items: flex-end;
}

/* Card Styles */
.card {
  border-radius: 12px;
  border: none;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.card:hover {
  transform: translateY(-2px);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
}

.card .card-header {
  border-radius: 12px 12px 0 0;
  border-bottom: 1px solid #e9ecef;
  padding: 1.25rem;
}

.card .card-header h5 {
  color: #00a8cc;
  font-weight: 600;
}

.card .card-body {
  padding: 1rem;
}

/* Form Styles */
.form-group {
  margin-bottom: 0.5rem;
}

.form-group label {
  font-weight: 600;
  color: #5e5873;
  margin-bottom: 0.5rem;
  font-size: 0.875rem;
}

.form-group .form-control,
.form-group .form-select {
  border: 2px solid #e9ecef;
  border-radius: 8px;
  padding: 0.5rem 0.75rem;
  transition: all 0.3s ease;
}

.form-group .form-control:focus,
.form-group .form-select:focus {
  border-color: #00a8cc;
  box-shadow: 0 0 0 0.2rem rgba(0, 168, 204, 0.15);
}

.form-group .form-control:disabled,
.form-group .form-select:disabled {
  background-color: #f8f9fa;
  opacity: 0.7;
}

.form-group .form-textarea {
  resize: vertical;
  min-height: 100px;
}

/* Button Styles */
.btn {
  border-radius: 8px;
  font-weight: 600;
  padding: 0.75rem 1.5rem;
  transition: all 0.3s ease;
}

.btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);
}

.btn.btn-primary {
  background: linear-gradient(135deg, #00a8cc, #008baa);
  border: none;
}

.btn.btn-primary:hover {
  background: linear-gradient(135deg, #008baa, #00a8cc);
}

.btn.btn-light {
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: #00a8cc;
}

.btn.btn-light:hover {
  background: rgba(255, 255, 255, 1);
}

/* Current Plan Styles */
.current-plan {
  background: linear-gradient(135deg, rgba(0, 168, 204, 0.1), rgba(0, 139, 170, 0.1));
  border: 2px solid rgba(0, 168, 204, 0.2);
  border-radius: 12px;
  padding: 0.75rem;
  transition: all 0.3s ease;
}

.current-plan:hover {
  background: linear-gradient(135deg, rgba(0, 168, 204, 0.15), rgba(0, 139, 170, 0.15));
  transform: translateY(-2px);
}

.current-plan p {
  font-size: 1.25rem;
  font-weight: 700;
}

/* Action Buttons */
.action-buttons {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  border-radius: 12px 0 0 0;
  box-shadow: -5px 0 20px rgba(0, 0, 0, 0.1);
  z-index: 1000;
}

/* Tags Input Styles */
.form-tags .b-form-tag {
  background: linear-gradient(135deg, #00a8cc, #008baa);
  color: white;
  border: none;
  border-radius: 20px;
  padding: 0.5rem 1rem;
  font-weight: 500;
  margin: 0.25rem;
}

.form-tags .b-form-tag .b-form-tag-remove {
  color: white;
  opacity: 0.8;
}

.form-tags .b-form-tag .b-form-tag-remove:hover {
  opacity: 1;
}

/* Switch Styles */
.custom-switch .custom-control-input:checked ~ .custom-control-label::before {
  background: linear-gradient(135deg, #00a8cc, #008baa);
  border-color: #00a8cc;
}

/* Responsive Design */
@media (max-width: 768px) {
  .profile-page {
    padding: 0.5rem;
  }
  
  .profile-header .profile-cover {
    height: 250px;
  }
  
  .profile-header .profile-cover .profile-content {
    flex-direction: column;
    align-items: center;
    text-align: center;
    gap: 1rem;
    padding: 1rem;
  }
  
  .profile-header .profile-cover .profile-info .profile-stats {
    justify-content: center;
    gap: 1rem;
  }
  
  .profile-header .profile-cover .profile-actions {
    flex-direction: column;
    width: 100%;
  }
  
  .profile-header .profile-cover .profile-actions .btn {
    width: 100%;
  }
  
  .profile-header .profile-cover .profile-info .profile-name {
    font-size: 1.5rem;
  }
  
  .action-buttons {
    position: relative;
    margin-top: 2rem;
    border-radius: 12px;
    box-shadow: 0 5px 20px rgba(0, 0, 0, 0.1);
  }
}

/* Animations */
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.card {
  animation: fadeInUp 0.6s ease-out;
}

.card:nth-child(1) { animation-delay: 0.1s; }
.card:nth-child(2) { animation-delay: 0.2s; }
.card:nth-child(3) { animation-delay: 0.3s; }
.card:nth-child(4) { animation-delay: 0.4s; }

/* Loading States */
.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn:disabled:hover {
  transform: none;
  box-shadow: none;
}

/* Badge Styles */
.badge {
  font-weight: 600;
  padding: 0.5rem 1rem;
  border-radius: 20px;
}

.badge.badge-light-primary {
  background: linear-gradient(135deg, rgba(0, 168, 204, 0.1), rgba(0, 139, 170, 0.1));
  color: #00a8cc;
}
</style>
